(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.SuttonRugCore = factory();
}(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const shapes = ['rectangle', 'square', 'runner', 'stair-runner'];
  const shapeLabels = { rectangle: 'Rectangle rug', square: 'Square rug', runner: 'Runner', 'stair-runner': 'Straight stair runner' };
  const extras = ['stainGuard', 'careKit'];
  const finishes = { taped: 'Taped edge', whipped: 'Whipped edge', easybind: 'Easybind rope edge', easypiping: 'Easypiping edge' };
  const defaults = { v: 4, whipping: 'nearest', product: 'wool-aspen-belle-mountain', width: 2000, length: 3000, shape: 'rectangle', endEdging: 'none', edging: '', innerEdging: '', piping: '', finish: 'taped', border: 40, extras: [] };
  const isPiping = edge => edge?.edgeType === 'piping' || edge?.family === 'Piping';
  function edgeAllowed(edge, finish, slot = 'edge') {
    if (!edge) return false;
    if (slot === 'piping') return isPiping(edge);
    if (finish === 'easybind' || finish === 'easypiping') return edge.edgeType === finish;
    if (edge.edgeType === 'easybind' || edge.edgeType === 'easypiping') return false;
    if (isPiping(edge)) return false;
    return finish === 'bound' || edge?.narrowOnly !== true;
  }
  function finishWidth(s) { return s.finish === 'easybind' ? 8 : s.finish === 'easypiping' ? 6 : s.finish === 'whipped' ? 6 : s.finish === 'bound' ? 12 : s.finish === 'double' ? 80 : s.finish === 'piped' ? 47 : 40; }
  function normaliseFinish(s, edgings) {
    if (!Object.hasOwn(finishes, s.finish)) s.finish = 'taped';
    const tape = edgings.find(x => x.family === 'Herringbone' && x.colour === 'Alpaca' && edgeAllowed(x,s.finish)) || edgings.find(x => edgeAllowed(x,s.finish));
    if (!edgeAllowed(edgings.find(x => x.id === s.edging),s.finish) || !edgings.some(x => x.id === s.edging)) s.edging = tape?.id;
    if (!edgings.some(x => x.id === s.innerEdging && edgeAllowed(x,'double'))) s.innerEdging = edgings.find(x => x.family === 'Herringbone' && x.colour === 'Pitch Black')?.id || tape?.id;
    if (!edgings.some(x => x.id === s.piping && isPiping(x))) s.piping = edgings.find(x => isPiping(x) && x.colour === 'Midnight')?.id || edgings.find(isPiping)?.id || '';
    s.border = finishWidth(s);
    return s;
  }
  function cleanState(input, products, edgings, threads = []) {
    const p = new Set(products.map(x => x.handle)), e = new Set(edgings.map(x => x.id));
    const s = Object.assign({}, defaults, { product: p.has(defaults.product) ? defaults.product : products[0]?.handle, edging: edgings.find(x => x.family === 'Herringbone' && x.colour === 'Alpaca')?.id || edgings[0]?.id });
    const warnings = [];
    if (!input || typeof input !== 'object' || ![1,2,3,4].includes(input.v)) {
      const limit = sizeLimits(products.find(x => x.handle === s.product)).width;
      if (limit) s.width = Math.min(s.width, limit);
      return { state: normaliseFinish(s,edgings), warnings: input ? ['This saved design could not be read. Please choose your design again.'] : [] };
    }
    if (p.has(input.product)) s.product = input.product; else warnings.push('The saved carpet is unavailable. Please choose a carpet.');
    if (e.has(input.edging)) s.edging = input.edging; else warnings.push('The saved edging is unavailable. Please choose an edging.');
    for (const key of ['width', 'length']) {
      if (Number.isInteger(input[key]) && input[key] >= 100 && input[key] <= 30000) s[key] = input[key];
      else warnings.push('A saved measurement was invalid. Please check the size.');
    }
    if (shapes.includes(input.shape)) s.shape = input.shape;
    if (input.v >= 4 && ['none', 'top', 'bottom', 'both'].includes(input.endEdging)) s.endEdging = input.endEdging;
    if (input.v >= 2 && Object.hasOwn(finishes,input.finish)) s.finish = input.finish;
    else if (input.v >= 2 && input.finish) warnings.push('This saved finish is no longer offered. Taped edge is selected; please review your design.');
    if (input.v === 1 && isPiping(edgings.find(x => x.id === input.edging))) { s.finish = 'piped'; s.piping = input.edging; }
    else if (input.v === 1 && edgings.find(x => x.id === input.edging)?.narrowOnly) s.finish = 'bound';
    if (!Object.hasOwn(finishes,s.finish)) warnings.push('This saved finish is no longer offered. Taped edge is selected; please review your design.');
    if (input.v >= 2 && e.has(input.innerEdging)) s.innerEdging = input.innerEdging;
    if (input.v >= 2 && e.has(input.piping)) s.piping = input.piping;
    if (input.v >= 3 && input.whipping && input.whipping !== 'nearest') {
      if (threads.some(t => t.id === input.whipping)) s.whipping = input.whipping;
      else warnings.push('The saved thread colour is unavailable. Nearest colour match is selected; please review it.');
    }
    if (Array.isArray(input.extras)) s.extras = extras.filter(x => input.extras.includes(x));
    if (s.shape === 'square') s.length = s.width;
    normaliseFinish(s,edgings);
    const sizeError = validate(s, products.find(x => x.handle === s.product));
    if (sizeError) warnings.push('Please review the saved size. ' + sizeError);
    return { state: s, warnings };
  }
  function fromInput(value, unit) {
    if (String(value).trim() === '') return null;
    const n = Number(value), mm = Math.round(n * (unit === 'm' ? 1000 : 10));
    return Number.isFinite(n) && Number.isSafeInteger(mm) && n > 0 ? mm : null;
  }
  function convertInput(value, from, to) {
    if (String(value).trim() === '') return '';
    const n = Number(value), factor = (from === 'm' ? 1000 : 10) / (to === 'm' ? 1000 : 10);
    return Number.isFinite(n) ? String(Number((n * factor).toFixed(6))) : '';
  }
  function sizeLimits(product) {
    const widths = (Array.isArray(product?.widths) ? product.widths : []).map(Number).filter(n => Number.isFinite(n) && n >= .1);
    return { width: widths.length ? Math.round(Math.max(...widths) * 1000) : null, length: 25000 };
  }
  function validate(s, product) {
    if (!Number.isSafeInteger(s.width) || !Number.isSafeInteger(s.length) || s.width < 100 || s.length < 100) return 'Enter a width and length of at least 10 cm (0.1 m).';
    const limits = sizeLimits(product);
    // Older pages may briefly retain the previous script during a live asset update.
    if (product !== undefined && !limits.width) return 'The roll width is unavailable for this carpet. Please choose another carpet or contact us.';
    if (limits.width && s.width > limits.width) return 'Maximum width for this carpet is ' + limits.width / 1000 + ' m (' + limits.width / 10 + ' cm). Please reduce the width.';
    if (s.length > limits.length) return 'Maximum rug length is 25 m (2,500 cm). Please reduce the length.';
    if (s.shape === 'stair-runner' && s.length < s.width) return 'For a stair runner, enter the narrow measurement as the width and the full stair run as the length.';
    if (finishWidth(s) * 2 >= Math.min(s.width, s.length)) return 'Increase the rug size to allow space for your selected finish.';
    return '';
  }
  function serialise(s) { return JSON.stringify({ v: 4, whipping: s.whipping || 'nearest', product: s.product, width: s.width, length: s.length, shape: s.shape, endEdging: s.shape === 'stair-runner' ? s.endEdging || 'none' : 'none', edging: s.edging, innerEdging: s.innerEdging, piping: s.piping, finish: s.finish, extras: extras.filter(x => s.extras.includes(x)) }); }
  function hash(s) { return '#rug=' + encodeURIComponent(serialise(s)); }
  function designURL(base, s, previewTheme) {
    const source = new URL(base), url = new URL(source.origin + source.pathname);
    if (/^\d+$/.test(String(previewTheme || ''))) {
      url.searchParams.set('view', 'sutton-rug-builder');
      url.searchParams.set('preview_theme_id', String(previewTheme));
    }
    url.hash = hash(s);
    return url.href;
  }
  function parseHash(h) { try { return h.startsWith('#rug=') && h.length < 5000 ? JSON.parse(decodeURIComponent(h.slice(5))) : null; } catch (_) { return { v: 0 }; } }
  function dimensions(s) { return (s.width / 10).toLocaleString('en-GB') + ' × ' + (s.length / 10).toLocaleString('en-GB') + ' cm'; }
  function geometry(s) {
    const scale = Math.min(500 / s.width, 450 / s.length);
    const w = s.width * scale, h = s.length * scale;
    return { x: (700 - w) / 2, y: (600 - h) / 2, w, h, b: finishWidth(s) * scale, scale };
  }
  function finishedEdges(s) {
    const stair = s.shape === 'stair-runner';
    const top = !stair || ['top', 'both'].includes(s.endEdging);
    const bottom = !stair || ['bottom', 'both'].includes(s.endEdging);
    return { left: true, right: true, top, bottom, lengthMm: 2 * s.length + (Number(top) + Number(bottom)) * s.width, corners: 2 * (Number(top) + Number(bottom)) };
  }
  function endLabel(s) {
    if (s.shape !== 'stair-runner') return 'All four edges finished';
    return { none: 'Long sides only; both short ends unedged', top: 'Long sides and top short end', bottom: 'Long sides and bottom short end', both: 'Long sides and both short ends' }[s.endEdging || 'none'];
  }
  // One path for the selected sides, reused by fabric, rope and piping previews.
  // Open short ends stay open at every border layer and in corner detail.
  function edgePath(s, offset, geometryOverride) {
    const g = geometryOverride || geometry(s), selected = finishedEdges(s);
    const x = g.x + offset, y = g.y + offset, w = g.w - offset * 2, h = g.h - offset * 2;
    if (selected.top && selected.bottom) return `M${x} ${y}H${x+w}V${y+h}H${x}Z`;
    if (selected.top) return `M${x} ${g.y+g.h}V${y}H${x+w}V${g.y+g.h}`;
    if (selected.bottom) return `M${x} ${g.y}V${y+h}H${x+w}V${g.y}`;
    return `M${x} ${g.y}V${g.y+g.h} M${x+w} ${g.y}V${g.y+g.h}`;
  }
  function rollWarning(s, product) {
    return validate(s, product);
  }
  function specification(s, product, edge, inner, piping, thread) {
    const options = { stainGuard: 'Intec Stain Guard', careKit: 'Carpet & upholstery stain kit' };
    const edgeDetails = (label, e) => e ? [label + ': ' + e.family + ' · ' + e.colour, 'Source: ' + e.url, e.sourceColour ? (e.brand || 'Jaspa Woven') + ' colour reference: ' + e.sourceColour : '', e.fibre ? 'Fibre: ' + e.fibre : ''].filter(Boolean).join('\n') : '';
    return [
      'Bespoke rug enquiry · Interiors By Sutton',
      'Carpet: ' + product.title,
      'Supplier: ' + product.brand,
      'Range / colour: ' + product.range + ' / ' + product.colour,
      'Carpet URL: ' + product.url,
      'Shape: ' + shapeLabels[s.shape],
      'Finished overall size: ' + dimensions(s) + ' (including the border)',
      'Area: ' + ((s.width * s.length) / 1e6).toFixed(2) + ' m²',
      'Edge finish: ' + finishes[s.finish],
      'Finished edges: ' + endLabel(s),
      'Edging length: ' + (finishedEdges(s).lengthMm / 1000).toFixed(3) + ' m before workshop allowance',
      s.finish === 'whipped' ? 'Whipping colour: ' + (s.whipping === 'nearest' || !thread ? 'Nearest colour match to the selected carpet' : thread.title + ' (Jaspa sewing-thread colour reference)') + '. Final thread match, yarn and suitability to be confirmed.' : edgeDetails(s.finish === 'double' ? 'Outer border' : (edge?.brand || 'Jaspa Woven') + ' edging',edge),
      s.finish === 'double' ? edgeDetails('Inner border',inner) : '',
      s.finish === 'piped' ? edgeDetails('Inner piping',piping) : '',
      // Fulfilment information is included in the enquiry, never a width control.
      ['taped','piped','double'].includes(s.finish) ? 'Workshop specification: standard 40 mm taped border'+(s.finish === 'double' ? ' for each tape' : '')+'; subject to confirmation.' : '',
      'Requested extras: ' + (extras.filter(x => s.extras.includes(x)).map(x => options[x]).join(', ') || 'None'),
      product.needsImageReview ? 'Image note: product photograph needs confirmation.' : '',
      rollWarning(s, product),
      'Please confirm suitability, finished border width, availability and quote. No price has been promised.'
    ].filter(Boolean).join('\n');
  }
  function matches(p, filters, type, ignore) {
    const values = { brand:[p.brand], range:[type === 'carpet' ? p.range : p.displayFamily || p.family], colour:p.colourGroups || [], material:p.materials || [], pattern:p.patterns || [] };
    for (const [key,value] of Object.entries(filters)) {
      if (!value || key === ignore) continue;
      if (key === 'search') {
        const haystack=[p.title,p.id,p.brand,p.range,p.family,p.colour,p.sourceColour,p.fibre,p.sku,...(p.tags || []),...Object.values(values).flat()].join(' ').toLocaleLowerCase('en-GB');
        if (!value.toLocaleLowerCase('en-GB').trim().split(/\s+/).every(q=>haystack.includes(q))) return false;
      } else if (!values[key]?.includes(value)) return false;
    }
    return true;
  }
  function facetOptions(items,filters,type,key) {
    const counts=new Map();
    for(const p of items.filter(p=>matches(p,filters,type,key))) {
      const values=key==='brand'?[p.brand]:key==='range'?[type==='carpet'?p.range:p.displayFamily || p.family]:key==='colour'?p.colourGroups:key==='material'?p.materials:p.patterns;
      for(const value of new Set(values || [])) if(value)counts.set(value,(counts.get(value)||0)+1);
    }
    if(filters[key]&&!counts.has(filters[key]))counts.set(filters[key],0);
    return [...counts].sort((a,b)=>a[0].localeCompare(b[0]));
  }
  return { defaults, shapes, shapeLabels, finishes, isPiping, edgeAllowed, finishWidth, normaliseFinish, cleanState, fromInput, convertInput, sizeLimits, validate, serialise, hash, designURL, parseHash, dimensions, geometry, finishedEdges, edgePath, endLabel, rollWarning, specification, matches, facetOptions };
}));
